// src/redux/sanctuary/sanctuarySlice.ts

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { AxiosError } from "axios";

import { sanctuaryApi } from "../../services/sanctuary/sanctuaryApi";
import type {
  SanctuaryRequestDTO,
  SanctuaryReviewDTO,
  SanctuaryOutcomeDTO,
  SanctuaryParticipationStatusDTO,
  SanctuaryReviewStatus,
} from "../../types/sanctuary/sanctuaryTypes";

type Status = "idle" | "loading" | "succeeded" | "failed";

/* ======================================================
   State
====================================================== */

interface SanctuaryState {
  requests: SanctuaryRequestDTO[];
  reviews: SanctuaryReviewDTO[];
  outcomes: SanctuaryOutcomeDTO[];
  participation?: SanctuaryParticipationStatusDTO;

  status: {
    requests: Status;
    reviews: Status;
    outcomes: Status;
    participation: Status;
    reviewSubmit: Status;
    optInOut: Status;
  };

  error?: string | null;
}

const initialState: SanctuaryState = {
  requests: [],
  reviews: [],
  outcomes: [],
  participation: undefined,
  status: {
    requests: "idle",
    reviews: "idle",
    outcomes: "idle",
    participation: "idle",
    reviewSubmit: "idle",
    optInOut: "idle",
  },
  error: null,
};

/* ======================================================
   Thunks
====================================================== */

// ---------------- Requests ----------------

export const fetchMySanctuaryRequests = createAsyncThunk<
  SanctuaryRequestDTO[],
  void,
  { rejectValue: any }
>("sanctuary/fetchRequests", async (_, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.listMyRequests();
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

// ---------------- Reviews ----------------

export const fetchMyCouncilReviews = createAsyncThunk<
  SanctuaryReviewDTO[],
  void,
  { rejectValue: any }
>("sanctuary/fetchReviews", async (_, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.listMyReviews();
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

export const submitCouncilReview = createAsyncThunk<
  SanctuaryReviewDTO,
  { reviewId: number; review_status: SanctuaryReviewStatus; comment?: string },
  { rejectValue: any }
>("sanctuary/submitReview", async (args, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.submitReview(args.reviewId, {
      review_status: args.review_status,
      comment: args.comment,
    });
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

// ---------------- Outcomes ----------------

export const fetchMyOutcomes = createAsyncThunk<
  SanctuaryOutcomeDTO[],
  void,
  { rejectValue: any }
>("sanctuary/fetchOutcomes", async (_, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.listMyOutcomes();
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

export const appealSanctuaryOutcome = createAsyncThunk<
  void,
  { outcomeId: number; message?: string },
  { rejectValue: any }
>("sanctuary/appealOutcome", async (args, { rejectWithValue }) => {
  try {
    await sanctuaryApi.appealOutcome(args.outcomeId, args.message);
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

// ---------------- Participation ----------------

export const fetchParticipationStatus = createAsyncThunk<
  SanctuaryParticipationStatusDTO,
  { lang?: string } | undefined,
  { rejectValue: any }
>("sanctuary/fetchParticipation", async (args, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.getParticipationStatus(args?.lang);
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

export const optInSanctuary = createAsyncThunk<
  SanctuaryParticipationStatusDTO,
  { policyId: number; lang?: string },
  { rejectValue: any }
>("sanctuary/optIn", async ({ policyId, lang }, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.optIn(policyId, lang);
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});


export const optOutSanctuary = createAsyncThunk<
  SanctuaryParticipationStatusDTO,
  void,
  { rejectValue: any }
>("sanctuary/optOut", async (_, { rejectWithValue }) => {
  try {
    return await sanctuaryApi.optOut();
  } catch (e) {
    const err = e as AxiosError;
    return rejectWithValue(err.response?.data);
  }
});

/* ======================================================
   Slice
====================================================== */

const sanctuarySlice = createSlice({
  name: "sanctuary",
  initialState,
  reducers: {
    clearSanctuaryError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* -------- Requests -------- */
      .addCase(fetchMySanctuaryRequests.pending, (s) => {
        s.status.requests = "loading";
        s.error = null;
      })
      .addCase(fetchMySanctuaryRequests.fulfilled, (s, a) => {
        s.requests = a.payload;
        s.status.requests = "succeeded";
      })
      .addCase(fetchMySanctuaryRequests.rejected, (s, a) => {
        s.status.requests = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to load sanctuary requests.";
      })

      /* -------- Reviews -------- */
      .addCase(fetchMyCouncilReviews.pending, (s) => {
        s.status.reviews = "loading";
        s.error = null;
      })
      .addCase(fetchMyCouncilReviews.fulfilled, (s, a) => {
        s.reviews = a.payload;
        s.status.reviews = "succeeded";
      })
      .addCase(fetchMyCouncilReviews.rejected, (s, a) => {
        s.status.reviews = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to load council reviews.";
      })

      .addCase(submitCouncilReview.pending, (s) => {
        s.status.reviewSubmit = "loading";
        s.error = null;
      })
      .addCase(submitCouncilReview.fulfilled, (s, a) => {
        const idx = s.reviews.findIndex(r => r.id === a.payload.id);
        if (idx >= 0) {
          s.reviews[idx] = a.payload;
        }
        s.status.reviewSubmit = "succeeded";
      })
      .addCase(submitCouncilReview.rejected, (s, a) => {
        s.status.reviewSubmit = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to submit review.";
      })

      /* -------- Outcomes -------- */
      .addCase(fetchMyOutcomes.pending, (s) => {
        s.status.outcomes = "loading";
        s.error = null;
      })
      .addCase(fetchMyOutcomes.fulfilled, (s, a) => {
        s.outcomes = a.payload;
        s.status.outcomes = "succeeded";
      })
      .addCase(fetchMyOutcomes.rejected, (s, a) => {
        s.status.outcomes = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to load sanctuary outcomes.";
      })

      /* -------- Participation -------- */
      .addCase(fetchParticipationStatus.pending, (s) => {
        s.status.participation = "loading";
        s.error = null;
      })
      .addCase(fetchParticipationStatus.fulfilled, (s, a) => {
        s.participation = a.payload;
        s.status.participation = "succeeded";
      })
      .addCase(fetchParticipationStatus.rejected, (s, a) => {
        s.status.participation = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to load participation status.";
      })

      .addCase(optInSanctuary.pending, (s) => {
        s.status.optInOut = "loading";
        s.error = null;
      })
      .addCase(optInSanctuary.fulfilled, (s, a) => {
        s.participation = a.payload;
        s.status.optInOut = "succeeded";
      })
      .addCase(optInSanctuary.rejected, (s, a) => {
        s.status.optInOut = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to opt-in sanctuary participation.";
      })

      // sanctuarySlice.ts
      .addCase(optOutSanctuary.pending, (s) => {
        s.status.optInOut = "loading";
        s.error = null;
      })
      .addCase(optOutSanctuary.fulfilled, (s, a) => {
        const prev = s.participation;

        if (!prev) {
          // Fallback safety: should rarely happen
          s.participation = a.payload;
        } else {
          s.participation = {
            ...prev,
            ...a.payload,

            // 🔐 Trust & eligibility flags must NEVER be downgraded by opt-out
            is_townlit_verified: prev.is_townlit_verified,
            is_verified_identity: prev.is_verified_identity,
            is_sanctuary_eligible: prev.is_sanctuary_eligible,
            eligible: prev.eligible,
          };
        }

        s.status.optInOut = "succeeded";
      })
      .addCase(optOutSanctuary.rejected, (s, a) => {
        s.status.optInOut = "failed";
        s.error =
          (a.payload as any)?.detail ||
          "Failed to opt-out sanctuary participation.";
      });

  },
});

export const { clearSanctuaryError } = sanctuarySlice.actions;
export default sanctuarySlice.reducer;
